import IconAndLabel from '@src/basic-components/IconAndLabel/IconAndLabel';
import JoinButtonModal from '@src/components/JoinButtonModal/JoinButtonModal';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import type { IconType } from '@src/icons/icons';
import { getMessageBoardReactionNames } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { getSuperCommentReactionCount } from '@src/model/super-comment/helpers';
import { isUserJoinedToToken } from '@src/model/user/helpers';
import type { ContentEntityType } from '@src/store/contentEntities/contentEntities';
import { getContentEntityReactionCount } from '@src/store/reactions/helpers/reactions';
import useMyReaction from '@src/store/reactions/hooks/useMyReaction';
import useReaction from '@src/store/reactions/hooks/useReaction';
import { abbreviatedFormatter } from '@src/utils/number-utils/number-utils';
import { toCamelCase } from '@src/utils/string-utils/string_utils';
import { getUrlPath } from '@src/utils/url-utils/url-utils';
import { type FC, memo, type PropsWithChildren, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';

export interface ReactionsProps {
    className?: string;
    tokenName: string;
    entityId: string;
    entityType: ContentEntityType;
    isDisabled?: boolean;
}

const Reactions: FC<PropsWithChildren<ReactionsProps>> = props => {
    const { tokenName, entityId, entityType } = props;

    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const navigate = useNavigate();

    const { handleModal } = useContext<UseModal>(ModalContext);

    const { react } = useReaction({ entityId, entityType });
    const { myReactionName } = useMyReaction({ entityId, entityType });

    function getLabel(reactionName: string): string {
        return `${abbreviatedFormatter(getContentEntityReactionCount(entityType, entityId, reactionName))}`;
    }

    const onReactionClick = useCallback(
        (reactionName: string) => (e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isLoggedIn) return navigate(`/bbs/login?next=${getUrlPath()}`);
            if (!isUserJoinedToToken(tokenName)) return handleModal(<JoinButtonModal tokenName={tokenName} />);
            react(myReactionName === reactionName ? '' : reactionName);
        },
        [tokenName, isLoggedIn, myReactionName]
    );

    function renderReaction(reactionName: string, i: number) {
        const { isDisabled } = props;
        const _isReacted = myReactionName === reactionName;

        const label = getLabel(reactionName) !== '0' ? getLabel(reactionName) : '';

        return (
            <IconAndLabel
                iconProps={{ className: 'text-lg' }}
                key={reactionName}
                labelProps={{ className: 'text-sm' }}
                iconName={toCamelCase(reactionName) as IconType}
                label={label}
                onClick={onReactionClick(reactionName)}
                isDisabled={isDisabled}
                isActive={_isReacted}
            />
        );
    }

    function renderReactions() {
        return getMessageBoardReactionNames().map((reactionName, i) => renderReaction(reactionName, i));
    }

    return renderReactions();
};

export default memo(Reactions);
